import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";

import { cn } from "../../utils";

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      // Checked bg was `bg-gray-300` (a plain pale gray, #F0F0F0) -- the old
      // Material Tailwind checkbox this replaced rendered its checked
      // background as `bg-primary` (the app's real brand pink, #E9366B; see
      // PR #887's removed select-controls/checkbox.tsx). Restored here for
      // brand parity, plus a matching checked border instead of the
      // otherwise-unconditional `border-gray-400`.
      //
      // Size restored to h-5 w-5 (20x20): the old MT-wrapped checkbox
      // rendered at 20x20 (see the size note in packages/ui/tailwind.config.mjs's
      // history/commit a62389ad) -- this was previously assumed to be an
      // accidental twMerge artifact, but real-world testing confirmed users
      // expect the 20x20 size, so matching it here as the deliberate size
      // rather than the raw-JSX-intended 16x16.
      //
      // Hover/active feedback restored: the old checkbox used `ripple`
      // (a circular highlight behind the box, MT's own internal markup) plus
      // `hover:before:opacity-5` and `checked:hover:bg-primary-200`, and (this
      // was missed in the first two attempts, found only by real
      // side-by-side screenshots via a scratch Playwright harness rendering
      // both the real old @material-tailwind/react component and this one)
      // a crisp pink ring on click from `focus:ring-1 focus:ring-primary-200`
      // -- MT used plain `focus:`, which fires on a mouse click too, not
      // `focus-visible:` (keyboard-only in modern browsers), which is what
      // this component had and is why no ring ever appeared on click. Bumped
      // the ripple circle's opacity up (10/20 read as basically invisible
      // next to the real thing) and switched the focus ring color off the
      // never-resolving `ring-ring` token onto `primary-200`, which actually
      // renders. Deliberately no `hover:border-*`/`hover:shadow-*` on the box
      // itself -- the old className never had one either, only the ripple
      // circle changes on hover.
      "peer relative z-0 h-5 w-5 shrink-0 rounded-xxs border border-gray-400 shadow transition-all duration-150 before:absolute before:inset-[-8px] before:-z-10 before:scale-75 before:rounded-full before:bg-gray-400 before:opacity-0 before:transition-all before:duration-150 before:content-[''] hover:before:scale-100 hover:before:opacity-20 active:before:scale-125 active:before:opacity-35 focus:outline-hidden focus:ring-1 focus:ring-primary-200 disabled:cursor-not-allowed disabled:opacity-50 disabled:before:opacity-0 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:before:bg-primary data-[state=checked]:hover:border-primary-200 data-[state=checked]:hover:bg-primary-200",
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className={cn("flex items-center justify-center text-current")}>
      <Check className="h-3.5 w-3.5" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };

